/**
 * Static puzzle catalog client.
 *
 * The hosted backend (Cloudflare Worker) cannot run the JAX-based
 * puzzle generators. Instead, scripts/generate_puzzle_catalog.py
 * pre-generates a pool of puzzles offline and writes per-type JSON
 * files into public/puzzles/. This client lazily loads those files
 * and returns a random matching entry on demand, replacing the
 * runtime /puzzle?... HTTP call.
 *
 * The return shape matches what server.py's /puzzle endpoint produces
 * so the puzzle init/load paths in App.js can swap fetch implementations
 * without other plumbing changes.
 */

const CATALOG_BASE = '/puzzles';
const VALID_TYPES = new Set(['win', 'threat', 'passthrough', 'denial']);

// One promise per type so concurrent callers share the same fetch.
const _typeCache = new Map();

function _loadType(puzzleType) {
  if (!VALID_TYPES.has(puzzleType)) {
    return Promise.reject(new Error(`Unknown puzzle type: ${puzzleType}`));
  }
  if (!_typeCache.has(puzzleType)) {
    const url = `${CATALOG_BASE}/${puzzleType}.json`;
    _typeCache.set(puzzleType, fetch(url, { cache: 'force-cache' }).then((r) => {
      if (!r.ok) throw new Error(`Catalog HTTP ${r.status} for ${puzzleType}`);
      return r.json();
    }).catch((err) => {
      // Drop the failed promise so a retry can re-fetch.
      _typeCache.delete(puzzleType);
      throw err;
    }));
  }
  return _typeCache.get(puzzleType);
}

/**
 * Resolve a puzzle for the given request.
 *
 * Params mirror the /puzzle?... query string used by the live backend:
 *   puzzle_type: 'win' | 'threat' | 'passthrough' | 'denial'
 *   num_jumps:   integer (within the type's range)
 *   player:      1 or 2 (matched against the entry's puzzle.player)
 *   seed:        optional; when omitted we pick a random entry
 *
 * Returns the same JSON shape as the FastAPI /puzzle endpoint, so
 * callers can keep using the response object's .board, .phutballPos,
 * .puzzle metadata, etc. without changes.
 */
export async function fetchPuzzleFromCatalog({ puzzle_type, num_jumps, player, seed }) {
  const typeData = await _loadType(puzzle_type);
  const bucket = typeData[String(num_jumps)] || [];
  if (bucket.length === 0) {
    throw new Error(`Catalog has no ${puzzle_type} puzzles at n=${num_jumps}`);
  }

  // Prefer entries matching the requested player. Fall back to "any"
  // if the player-specific subset is empty (the catalog generator
  // randomises player per entry, so very small buckets may not cover
  // both sides).
  const wantPlayer = Number(player);
  const matching = Number.isFinite(wantPlayer)
    ? bucket.filter((entry) => entry?.puzzle?.player === wantPlayer)
    : bucket;
  const pool = matching.length > 0 ? matching : bucket;

  // Use the user's seed (if any) to deterministically pick an index.
  // The catalog itself doesn't store puzzles by seed, but indexing by
  // (seed % pool.length) gives stable reproduction for the same seed.
  let idx;
  if (Number.isFinite(Number(seed))) {
    const s = Number(seed);
    idx = ((s % pool.length) + pool.length) % pool.length;
  } else {
    idx = Math.floor(Math.random() * pool.length);
  }
  const chosen = pool[idx];

  // Deep-clone so callers mutating the returned object (e.g.
  // engine.loadSnapshot reading board cells) don't poison the cache.
  return JSON.parse(JSON.stringify(chosen));
}

/**
 * Diagnostic helper for surfacing catalog availability to the UI.
 * Returns { type: { n: count } } once the index has loaded.
 */
export async function loadCatalogIndex() {
  const r = await fetch(`${CATALOG_BASE}/index.json`, { cache: 'force-cache' });
  if (!r.ok) throw new Error(`Catalog index HTTP ${r.status}`);
  return r.json();
}
